import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, CheckCircle2, Handshake, Rocket, Users, Layers } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import CountrySelect from "./CountrySelect";

const PARTNER_TYPES = [
  { id: "technology", label: "Technology Partner", icon: Rocket,
    desc: "Co-build on the CloudLab AI stack." },
  { id: "channel",    label: "Channel & Reseller Partner", icon: Users,
    desc: "Take our products to your market — earn on every deal." },
  { id: "backoffice", label: "Backoffice Partnership", icon: Layers,
    desc: "You stay client-facing. We deliver silently in the background." },
];

/**
 * <PartnerDrawer /> — slide-in-from-right partner application form.
 * Any button on the site can trigger it via:
 *   window.dispatchEvent(new CustomEvent("open-partner-drawer", {
 *     detail: { types: ["technology"] }   // pre-selects
 *   }))
 */
export default function PartnerDrawer() {
  const [open, setOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "", company: "", email: "",
    phoneDial: "+44", phoneLocal: "",
    country: "", website: "",
    types: [], description: "",
  });

  useEffect(() => {
    const handler = (e) => {
      const preTypes = e?.detail?.types || [];
      setForm((f) => ({ ...f, types: preTypes.length ? preTypes : f.types }));
      setSubmitted(false);
      setOpen(true);
    };
    window.addEventListener("open-partner-drawer", handler);
    return () => window.removeEventListener("open-partner-drawer", handler);
  }, []);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  const toggleType = (id) => {
    setForm((f) => ({
      ...f,
      types: f.types.includes(id) ? f.types.filter((t) => t !== id) : [...f.types, id],
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.types.length === 0) {
      toast.error("Please pick at least one partnership type.");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name,
        company: form.company,
        email: form.email,
        phone: `${form.phoneDial} ${form.phoneLocal}`.trim(),
        country: form.country,
        website: form.website,
        types: form.types,
        description: form.description,
      };
      await api.post("/partner-applications", payload);
      setSubmitted(true);
      toast.success("Partner application submitted. We'll be in touch within 24 hours.");
    } catch {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const close = () => setOpen(false);

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-[95]" data-testid="partner-drawer">
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }} onClick={close}
            className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
          />
          <motion.aside
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="absolute right-0 top-0 bottom-0 w-full sm:max-w-xl lg:max-w-2xl bg-background border-l border-border shadow-[0_25px_80px_-20px_rgba(0,0,0,0.4)] overflow-y-auto"
            role="dialog" aria-label="Become a partner"
          >
            <div className="sticky top-0 z-10 bg-background/95 backdrop-blur-sm border-b border-border px-5 md:px-10 py-4 md:py-5 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-[0.25em] text-primary">
                  <Handshake className="w-3 h-3" /> Partner with CloudLab
                </div>
                <h2 className="mt-1 font-heading font-black text-xl md:text-2xl tracking-tight leading-tight">
                  Tell us about your business.
                </h2>
              </div>
              <button onClick={close} data-testid="partner-drawer-close"
                className="shrink-0 w-10 h-10 rounded-full border border-border hover:bg-foreground/10 inline-flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>

            {submitted ? (
              <div className="px-6 md:px-10 py-16 text-center" data-testid="partner-drawer-submitted">
                <div className="w-16 h-16 mx-auto rounded-full bg-primary/10 flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-primary" />
                </div>
                <div className="mt-6 font-heading font-black text-2xl md:text-3xl tracking-tight">Thanks — we&apos;re on it.</div>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto leading-relaxed">
                  A senior member of the partnerships team is reviewing your application. Expect a personal reply within 24 hours at <strong>{form.email}</strong>.
                </p>
                <button onClick={close} data-testid="partner-drawer-done"
                  className="mt-8 rounded-full px-6 py-3 border border-border text-sm hover:bg-foreground/5">
                  Back to the site
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="px-5 md:px-10 py-6 md:py-8 space-y-6" data-testid="partner-drawer-form">
                {/* Partnership types (multi-select) */}
                <div>
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
                    Which partnership(s) are you interested in? <span className="text-primary">*</span>
                  </label>
                  <div className="mt-3 grid gap-2.5">
                    {PARTNER_TYPES.map((t) => {
                      const Icon = t.icon;
                      const active = form.types.includes(t.id);
                      return (
                        <button
                          key={t.id} type="button" onClick={() => toggleType(t.id)}
                          data-testid={`partner-type-checkbox-${t.id}`}
                          aria-pressed={active}
                          className={`w-full text-left flex items-start gap-3 rounded-xl border-2 p-3 md:p-4 transition-all ${active ? "border-primary bg-primary/[0.06]" : "border-border hover:border-primary/50"}`}
                        >
                          <div className={`shrink-0 w-9 h-9 rounded-xl grid place-items-center transition-colors ${active ? "bg-primary text-primary-foreground" : "bg-primary/10 text-primary"}`}>
                            <Icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-sm">{t.label}</div>
                              <div className={`ml-auto w-5 h-5 rounded-md border-2 grid place-items-center transition-colors ${active ? "border-primary bg-primary" : "border-border"}`}>
                                {active && <CheckCircle2 className="w-3.5 h-3.5 text-primary-foreground" />}
                              </div>
                            </div>
                            <div className="mt-0.5 text-xs text-muted-foreground leading-relaxed">{t.desc}</div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* KYC block */}
                <div className="pt-2 border-t border-border">
                  <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-primary mb-4 mt-4">KYC — who are you?</div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <Field label="Full name" required value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="partner-name" />
                    <Field label="Company" required value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="partner-company" />
                    <Field label="Work email" required type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="partner-email" />
                    <Field label="Company website" value={form.website} onChange={(v) => setForm({ ...form, website: v })} placeholder="https://" testid="partner-website" />
                    {/* Phone with dial-code */}
                    <div>
                      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Phone <span className="text-primary">*</span></label>
                      <div className="mt-2 flex gap-2">
                        <div className="shrink-0 w-[120px]">
                          <CountrySelect
                            mode="dial" value={form.phoneDial}
                            onChange={(dial) => setForm({ ...form, phoneDial: dial })}
                            testid="partner-phone-dial" placeholder="+"
                          />
                        </div>
                        <input
                          required type="tel" value={form.phoneLocal}
                          onChange={(e) => setForm({ ...form, phoneLocal: e.target.value })}
                          placeholder="7380 885 506"
                          className="flex-1 bg-background border border-border rounded-xl px-4 py-2.5 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                          data-testid="partner-phone-input"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">Country <span className="text-primary">*</span></label>
                      <div className="mt-2">
                        <CountrySelect
                          mode="country" value={form.country}
                          onChange={(name) => setForm({ ...form, country: name })}
                          testid="partner-country" placeholder="Select your country"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Description */}
                <div className="pt-2 border-t border-border">
                  <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mt-4 block">
                    What are you looking for from CloudLab? <span className="text-primary">*</span>
                  </label>
                  <textarea
                    required rows={5}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    placeholder="Tell us about your business, the markets you serve, and how you'd like to partner with us."
                    className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-3 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 resize-none"
                    data-testid="partner-description-input"
                  />
                </div>

                <div className="sticky bottom-0 bg-background/95 backdrop-blur-sm -mx-5 md:-mx-10 px-5 md:px-10 py-4 border-t border-border">
                  <button
                    disabled={loading} type="submit"
                    className="w-full rounded-full px-6 py-4 bg-primary text-primary-foreground text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50 shadow-[0_15px_40px_-15px_hsl(var(--primary)/0.5)]"
                    data-testid="partner-drawer-submit"
                  >
                    {loading ? "Submitting…" : "Submit partner application"} <ArrowRight className="w-4 h-4" />
                  </button>
                  <div className="mt-2 text-center text-[11px] text-muted-foreground">
                    We reply personally within 24 hours.
                  </div>
                </div>
              </form>
            )}
          </motion.aside>
        </div>
      )}
    </AnimatePresence>
  );
}

function Field({ label, value, onChange, required, type = "text", testid, placeholder }) {
  return (
    <div>
      <label className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground">
        {label}{required && <span className="text-primary"> *</span>}
      </label>
      <input
        required={required} type={type} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full bg-background border border-border rounded-xl px-4 py-2.5 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
        data-testid={`${testid}-input`}
      />
    </div>
  );
}
